<template>
  <div :class="[classes]">
    <div class="relative w-full">
      <Listbox as="div" class="w-full mr-5">
        <ListboxLabel v-if="showLabel" class="block text-xs font-medium text-common-grey">
          {{ label }}
        </ListboxLabel>
        <div class="relative w-full">
          <ListboxButton
            :disabled="disabled"
            :class="[disabled ? 'text-common-grey' : '', inputClasses, 'bg-light-box-background dark:bg-dark-box-background']"
            class="relative w-full px-4 py-0 font-medium transition ease-in-out min-w-max duration-side-menu-transition-duration">
            <span v-if="icon" class="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
              <component :is="icon" class="w-5 h-5 text-gray-400" aria-hidden="true" />
            </span>
            <span v-if="selected_items?.length > 0" class="block truncate mx-1 text-base dark:text-white" :class="{ 'pl-5': icon }">{{
              getSelectionLabel()
            }}</span>
            <span v-else class="block truncate text-gray-400 text-sm" :class="{ 'pl-5': icon }">{{
              !isOnCompany ? translatedPlaceholder : ""
            }}</span>
            <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronDown
                v-if="!(hideChevronOnDisabled && disabled)"
                :class="isOnCompany && 'translate-y-[-6px]'"
                class="w-5 h-5 text-gray-400"
                aria-hidden="true" />
            </span>
          </ListboxButton>
          <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <ListboxOptions
              v-if="items?.length > 0"
              style="height: 200px"
              :class="[{ relative: relativePos }, { 'bg-light-box-background dark:bg-dark-box-background': !isBoxBackground }]"
              class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background dark:text-white max-h-56 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              <ListboxOption
                v-if="selected_items?.length > 0 && allowNull"
                key="reset"
                v-slot="{ active }"
                @click="
                  () => {
                    selected_items = []
                    setValue([])
                    $emit('update:currentItems', [])
                  }
                ">
                <li
                  :class="[
                    active
                      ? 'text-dark-body-text bg-light-brand-blue dark:text-light-body-text dark:bg-dark-brand-blue'
                      : 'text-light-body-text dark:text-dark-body-text',
                    'rounded-lg  text-light-body-text dark:text-dark-body-text hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue transition ease-in-out duration-500 cursor-default relative py-2 pl-8 pr-4'
                  ]">
                  <span class="block font-normal text-right truncate opacity-50">
                    {{ translatedClearLabel }}
                  </span>
                </li>
              </ListboxOption>
              <ListboxOption v-if="!all_selected"
                             v-slot="{ active }"
                             as="template"
                             class="group"
                             @click="handleSelect(null, true)">
                <li
                  :class="[
                    active
                      ? 'text-dark-body-text bg-light-brand-blue dark:text-light-body-text dark:bg-dark-brand-blue'
                      : 'text-light-body-text dark:text-dark-body-text',
                    'rounded-lg  text-light-body-text dark:text-dark-body-text hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue transition ease-in-out duration-500 cursor-default relative py-2 pl-8 pr-4'
                  ]">
                  <span :class="[all_selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                    {{ all_selected ? "Deselect All" : "Select All" }}
                  </span>

                  <span
                    v-if="all_selected"
                    :class="[
                      active ? 'text-light-body-text dark:text-dark-body-text' : 'text-light-brand-blue dark:text-dark-brand-blue',
                      'absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration'
                    ]">
                    <Check class="w-5 h-5" aria-hidden="true" />
                  </span>
                  <span v-else>
                    <Check
                      :class="[
                        active ? 'opacity-100 text-light-body-text dark:text-dark-body-text' : 'text-common-grey',
                        'opacity-0 group-hover:text-white absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration'
                      ]"
                      class="h-full w-7 absolute inset-y-0 left-0 flex items-center pl-1.5"
                      aria-hidden="true" />
                  </span>
                </li>
              </ListboxOption>
              <ListboxOption
                v-for="item in items"
                :key="item.id"
                v-slot="{ active }"
                as="template"
                class="group"
                :value="item"
                @click="handleSelect(item)">
                <li
                  :class="[
                    active
                      ? 'text-dark-body-text bg-light-brand-blue dark:text-light-body-text dark:bg-dark-brand-blue'
                      : 'text-light-body-text dark:text-dark-body-text',
                    'rounded-lg  text-light-body-text dark:text-dark-body-text hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue transition ease-in-out duration-500 cursor-default relative py-2 pl-8 pr-4'
                  ]">
                  <span :class="[isSelected(item) ? 'font-semibold' : 'font-normal', 'block truncate']">
                    {{ getLabel(item) }}
                  </span>

                  <span
                    v-if="isSelected(item)"
                    :class="[
                      active ? 'text-light-body-text dark:text-dark-body-text' : 'text-light-brand-blue dark:text-dark-brand-blue',
                      'absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration'
                    ]">
                    <Check class="w-5 h-5" aria-hidden="true" />
                  </span>
                  <span v-else>
                    <Check
                      :class="[
                        active ? 'opacity-100 text-light-body-text dark:text-dark-body-text' : 'text-common-grey',
                        'opacity-0 group-hover:text-white absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration'
                      ]"
                      class="h-full w-7 absolute inset-y-0 left-0 flex items-center pl-1.5"
                      aria-hidden="true" />
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
      <p
        class="first-letter:capitalize absolute right-0 leading-tight pt-[1px] text-[12px] text-red-400"
        :class="[errorMessage || meta.valid ? 'visible' : 'invisible']">
        {{ errorMessage || successMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable"
import { useField } from "vee-validate"
import { ChevronDown, Check } from "lucide-vue-next"
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue"
export default {
  components: {
    draggable,
    Listbox,
    ListboxLabel,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    ChevronDown,
    Check
  },
  props: {
    relativePos: null,
    icon: null,
    items: {
      type: Array,
      default: () => []
    },
    showLabel: {
      type: Boolean,
      default: false
    },
    inputClasses: {
      type: String,
      default: ""
    },
    classes: {
      type: String,
      default: ""
    },
    value: {
      type: Array,
      default: () => []
    },
    name: {
      type: String,
      required: true
    },
    clearLabel: {
      type: String,
      default: "Clear"
    },
    label: {
      type: String,
      required: true
    },
    labelAttr: {
      type: String,
      validator(v) {
        return v !== undefined
      }
    },
    valueAttr: {
      validator(v) {
        return v !== undefined
      }
    },
    disabled: {
      type: Boolean,
      required: false
    },
    hideChevronOnDisabled: {
      type: Boolean,
      required: false
    },
    successMessage: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: "Select items"
    },
    selectionText: {
      type: String,
      default: "Selected (%COUNT)"
    },
    allowNull: {
      type: Boolean,
      default: true
    },
    returnObjects: {
      type: Boolean,
      default: false
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    isOnCompany: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:currentItems"],
  setup(props) {
    const {
      value: selected_items,
      errorMessage,

      setValue,
      meta
    } = useField(props.name, undefined, {
      initialValue: props.value ? props.value : []
    })
    return {
      setValue,
      errorMessage,
      selected_items,
      meta
    }
  },
  data() {
    return {
      items_clone: []
    }
  },
  computed: {
    translatedClearLabel() {
      return this.$t(this.clearLabel)
    },
    translatedPlaceholder() {
      return this.$t(this.placeholder)
    },
    all_selected() {
      if (!this.selected_items?.length) {
        return false
      }
      return this.selected_items.length == this.items?.length
    }
  },
  watch: {
    value() {
      this.setValue(this.value)
    }
  },
  created() {
    this.items_clone = this.$utils.clone(this.items)
  },
  methods: {
    isSelected(value) {
      return this.selected_items?.findIndex((item) => this.getValue(item) === this.getValue(value)) != -1 ? true : false
    },
    handleSelect(item, select_all_flag) {
      if (select_all_flag) {
        this.selected_items = []
        if (!this.all_selected) {
          this.items.forEach((item) => {
            this.selected_items.push(item)
          })
        }
      } else {
        if (this.isSelected(item)) {
          const index = this.selected_items.findIndex((it) => this.getValue(it) == this.getValue(item))
          this.selected_items.splice(index, 1)
        } else {
          this.selected_items.push(item)
        }
      }

      if (this.returnObjects) {
        this.$emit("update:currentItems", this.selected_items)
      } else {
        this.$emit(
          "update:currentItems",
          this.selected_items.map((x) => this.getValue(x))
        )
      }
    },
    getSelectionLabel() {
      if (this.selected_items?.length > 0) {
        if (this.selectionText == null) {
          if (this.all_selected) {
            return "All Selected"
          }
          return this.items
            .filter((item) => this.selected_items.findIndex((selected_item) => this.getValue(selected_item) == this.getValue(item)) != -1)
            .map((x) => this.getLabel(x))
            .join(",")
        } else {
          return this.selectionText.replace("%COUNT", this.selected_items?.length)
        }
      } else {
        return this.translatedPlaceholder
      }
    },
    getLabel(item) {
      if (typeof item == "object" && this.labelAttr) {
        return item[this.labelAttr]
      }
      return item
    },
    getValue(item) {
      if (typeof item == "object" && this.valueAttr) {
        if (item) {
          return item[this.valueAttr]
        }
      }
      return item
    }
  }
}
</script>
